import React, { useState, useEffect } from "react";
import { networkAPI, activityAPI } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import {
  Network,
  ArrowRight,
  Clock,
  Activity,
  Globe,
  Server,
  Users,
  Search,
  LogIn,
  Eye,
  Edit,
  Plus,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const actionIcons = {
  login: LogIn,
  view: Eye,
  update: Edit,
  create: Plus,
  export: Download,
};

export const NetworkActivityPage = () => {
  // Network state
  const [traffic, setTraffic] = useState([]);
  const [networkLoading, setNetworkLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Activity state
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchTraffic();
    fetchActivities();
    const interval = setInterval(() => fetchTraffic(), 30000);
    return () => clearInterval(interval);
  }, [currentPage]);

  // ==================== NETWORK ====================

  const fetchTraffic = async (page = currentPage) => {
    try {
      const response = await networkAPI.getAll(page, 20);
      setTraffic(response.items);
      setTotalPages(response.total_pages || 1);
    } catch (err) {
      toast.error("Failed to fetch network traffic");
    } finally {
      setNetworkLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getProtocolColor = (protocol) => {
    switch (protocol.toLowerCase()) {
      case "https":
        return "bg-green-100 text-green-800";
      case "ssh":
        return "bg-red-100 text-red-800";
      case "mysql":
        return "bg-blue-100 text-blue-800";
      case "dns":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const isExternalIP = (ip) => {
    if (!ip) return false;
    if (
      ip.startsWith("192.168.") ||
      ip.startsWith("10.") ||
      ip === "127.0.0.1" ||
      ip.startsWith("::") ||
      ip === "0.0.0.0"
    )
      return false;
    if (ip.startsWith("172.")) {
      const second = parseInt(ip.split(".")[1], 10);
      if (second >= 16 && second <= 31) return false;
    }
    if (ip.startsWith("169.254.")) return false;
    return true;
  };

  const totalBytes = traffic.reduce((acc, t) => acc + t.bytes_transferred, 0);
  const totalPackets = traffic.reduce((acc, t) => acc + t.packets, 0);
  const externalConnections = traffic.filter(
    (t) => isExternalIP(t.source_ip) || isExternalIP(t.destination_ip),
  ).length;

  // ==================== ACTIVITY ====================

  const fetchActivities = async () => {
    try {
      const response = await activityAPI.getAll();
      setActivities(response.items);
    } catch (err) {
      toast.error("Failed to fetch user activity");
    } finally {
      setActivityLoading(false);
    }
  };

  const filteredActivities = activities.filter((activity) => {
    return (
      activity.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.user_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.action.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getActionColor = (action) => {
    switch (action) {
      case "login":
        return "bg-green-100 text-green-800";
      case "view":
        return "bg-blue-100 text-blue-800";
      case "update":
        return "bg-amber-100 text-amber-800";
      case "create":
        return "bg-purple-100 text-purple-800";
      case "export":
        return "bg-slate-100 text-slate-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase() || "U"
    );
  };

  const uniqueUsers = [
    ...new Map(activities.map((a) => [a.user_email, a])).values(),
  ];

  const loading = networkLoading && activityLoading;

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="network-activity-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ==================== RENDER ====================

  return (
    <div className="space-y-6" data-testid="network-activity-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
          Network & Activity
        </h1>
        <p className="text-muted-foreground mt-1">
          Monitor network traffic and user activity
        </p>
      </div>

      <Tabs defaultValue="network" className="space-y-6">
        <TabsList>
          <TabsTrigger value="network" data-testid="tab-network">
            <Network className="w-4 h-4 mr-2" />
            Network
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">
            <Users className="w-4 h-4 mr-2" />
            User Activity
          </TabsTrigger>
        </TabsList>

        {/* ==================== NETWORK TAB ==================== */}
        <TabsContent value="network" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              className="border-border shadow-sm"
              data-testid="connections-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Connections
                    </p>
                    <p className="font-heading text-3xl font-bold">
                      {traffic.length}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-primary/10">
                    <Network className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border shadow-sm" data-testid="traffic-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Data Transferred
                    </p>
                    <p className="font-heading text-3xl font-bold">
                      {formatBytes(totalBytes)}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-blue-100">
                    <Activity className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border shadow-sm" data-testid="packets-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Packets
                    </p>
                    <p className="font-heading text-3xl font-bold">
                      {totalPackets.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-purple-100">
                    <Server className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card
              className="border-border shadow-sm"
              data-testid="external-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      External Connections
                    </p>
                    <p className="font-heading text-3xl font-bold">
                      {externalConnections}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-amber-100">
                    <Globe className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Traffic Table */}
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">
                Recent Traffic ({traffic.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Connection</TableHead>
                      <TableHead>Protocol</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Packets</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {traffic.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center py-8 text-muted-foreground"
                        >
                          No network traffic found
                        </TableCell>
                      </TableRow>
                    ) : (
                      traffic.map((item) => (
                        <TableRow
                          key={item.id}
                          data-testid={`traffic-row-${item.id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2">
                                {isExternalIP(item.source_ip) ? (
                                  <Globe className="w-4 h-4 text-amber-500" />
                                ) : (
                                  <Server className="w-4 h-4 text-slate-400" />
                                )}
                                <code className="ip-address">
                                  {item.source_ip}
                                </code>
                              </div>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              <div className="flex items-center gap-2">
                                {isExternalIP(item.destination_ip) ? (
                                  <Globe className="w-4 h-4 text-amber-500" />
                                ) : (
                                  <Server className="w-4 h-4 text-slate-400" />
                                )}
                                <code className="ip-address">
                                  {item.destination_ip}
                                </code>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={`${getProtocolColor(item.protocol)}`}
                            >
                              {item.protocol}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-sm">
                              {formatBytes(item.bytes_transferred)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {item.packets.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {formatTimeAgo(item.timestamp)}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}

          {/* Protocol Breakdown */}
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">
                Protocol Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[...new Set(traffic.map((t) => t.protocol))].map(
                  (protocol) => {
                    const protocolTraffic = traffic.filter(
                      (t) => t.protocol === protocol,
                    );
                    const bytes = protocolTraffic.reduce(
                      (acc, t) => acc + t.bytes_transferred,
                      0,
                    );
                    return (
                      <div
                        key={protocol}
                        className="p-4 rounded-lg border border-border"
                        data-testid={`protocol-${protocol.toLowerCase()}`}
                      >
                        <Badge
                          className={`${getProtocolColor(protocol)} mb-2`}
                        >
                          {protocol}
                        </Badge>
                        <p className="font-heading text-2xl font-bold">
                          {protocolTraffic.length}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatBytes(bytes)}
                        </p>
                      </div>
                    );
                  },
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== ACTIVITY TAB ==================== */}
        <TabsContent value="activity" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card
              className="border-border shadow-sm"
              data-testid="total-users-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Users
                    </p>
                    <p className="font-heading text-3xl font-bold">
                      {uniqueUsers.length}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-primary/10">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card
              className="border-border shadow-sm"
              data-testid="active-sessions-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Active Sessions
                    </p>
                    <p className="font-heading text-3xl font-bold">
                      {activities.filter((a) => a.action === "login").length}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-green-100">
                    <LogIn className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card
              className="border-border shadow-sm"
              data-testid="total-actions-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Actions
                    </p>
                    <p className="font-heading text-3xl font-bold">
                      {activities.length}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-blue-100">
                    <Eye className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users List */}
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">
                Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {uniqueUsers.map((user, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    data-testid={`user-card-${index}`}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {getInitials(user.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{user.user_name}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {user.user_email}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Search */}
          <Card className="border-border shadow-sm">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search activity by user, email, or action..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="activity-search-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Activity Table */}
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">
                Recent Activity ({filteredActivities.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredActivities.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center py-8 text-muted-foreground"
                        >
                          No activity found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredActivities.map((activity) => {
                        const ActionIcon =
                          actionIcons[activity.action] || Eye;
                        return (
                          <TableRow
                            key={activity.id}
                            data-testid={`activity-row-${activity.id}`}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                    {getInitials(activity.user_name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm">
                                    {activity.user_name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {activity.user_email}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={`${getActionColor(activity.action)} capitalize`}
                              >
                                <ActionIcon className="w-3 h-3 mr-1" />
                                {activity.action}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {activity.resource}
                              </span>
                            </TableCell>
                            <TableCell>
                              <code className="ip-address">
                                {activity.ip_address}
                              </code>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                {formatTimeAgo(activity.timestamp)}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
